import { useEffect, useRef } from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import { useAuthStore } from "@/stores/useAuthStore";
import { setPrivyTokenGetter, clearTokenCache } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { AuthSession, UserRole, UserTier } from "@/lib/types";
import { sessionAfterProfileSync, syncedTier } from "@/lib/tier";
import {
  resolveEmail,
  resolveDisplayName,
  type PrivyIdentitySource,
} from "@/lib/privyIdentity";
import { usePolygonClient } from "@/hooks/usePolygonClient";
import { supabase } from "@/lib/supabase";

/**
 * How long the role fetch may take before we give up on it.
 *
 * Deliberately far below the default edge-function ceiling. `profileSynced`
 * gates AuthGuard's spinner, useFrictionlessOnboarding and
 * useChainOverridePolicy, so a stalled `profiles` call does not merely delay
 * one request -- it holds the whole app in "role unknown". Failing fast and
 * falling back to the non-admin default beats an indefinite spinner.
 */
const ROLE_FETCH_TIMEOUT_MS = 15_000;

/**
 * Bridges Privy auth state into the Zustand store and wires the Supabase
 * client's token exchange. Also syncs the user profile to the database
 * on first login and registers the user on-chain.
 *
 * Call this once inside the PrivyProvider tree.
 * All downstream consumers (useAuth, AuthGuard) read from the store.
 */
export function usePrivySync(): void {
  const { ready, authenticated, user, getAccessToken } = usePrivy();
  const { wallets } = useWallets();
  const embeddedWalletAddress = pickEmbeddedWalletAddress(user, wallets);
  const setSession = useAuthStore((s) => s.setSession);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setOnboarded = useAuthStore((s) => s.setOnboarded);
  const setProfileSynced = useAuthStore((s) => s.setProfileSynced);
  const initialSyncDone = useRef(false);
  const lastSyncedAddress = useRef<string | null>(null);
  const emailSynced = useRef(false);
  const chainRegistered = useRef<string | null>(null);
  const polygonClient = usePolygonClient();

  // Wire the Privy token getter into the Supabase client once on mount
  useEffect(() => {
    setPrivyTokenGetter(getAccessToken);
    return () => clearTokenCache();
  }, [getAccessToken]);

  useEffect(() => {
    if (!ready) {
      setLoading(true);
      return;
    }

    setLoading(false);

    if (authenticated && user) {
      // Extract email from any linked account — user.google / user.email
      // may not be populated on the first render while Privy still loads
      // linked accounts. linkedAccounts is the authoritative source.
      //
      // Since DEV-767 enabled SMS login this can legitimately be "" forever:
      // a phone-only user has no email at all, so the late-arrival re-sync
      // below simply never fires for them.
      const email = resolveEmail(user as PrivyIdentitySource);

      // Preserve the role we previously fetched from the profile so that
      // re-runs of this effect (e.g. when `wallets` or `polygonClient`
      // change reference) don't clobber an admin role back to "user".
      // The role fetch below is gated by `profileSynced.current`, so
      // without this we'd lose the role permanently after the first sync.
      const existingSession = useAuthStore.getState().session;
      const preservedRole: UserRole =
        existingSession?.userId === user.id ? existingSession.role : "user";
      // Same for the profile tier: undefined until the first sync answers.
      const preservedTier: UserTier | undefined =
        existingSession?.userId === user.id ? existingSession.tier : undefined;

      const session: AuthSession = {
        userId: user.id,
        email,
        displayName: resolveDisplayName(user as PrivyIdentitySource),
        avatarUrl: null,
        // The Privy embedded EOA is the on-chain msg.sender for
        // sponsored transactions (we deliberately do not use a smart
        // account — see DEV-242). Null until the embedded wallet
        // resolves; downstream code treats null as "wallet not ready
        // yet" and disables on-chain actions accordingly.
        walletAddress: embeddedWalletAddress ?? null,
        role: preservedRole,
        tier: preservedTier,
        createdAt: new Date().toISOString(),
      };

      setSession(session);

      // Initial profile sync — fires once without wallet_address so that
      // profileSynced becomes true and AuthGuard stops showing a spinner.
      // We must not gate this on embeddedWalletAddress, otherwise login is
      // blocked until Privy finishes resolving the embedded wallet.
      if (!initialSyncDone.current) {
        initialSyncDone.current = true;
        callEdgeFunction<{ role?: string; tier?: string; is_onboarded?: boolean }>("profiles", {
          body: {
            display_name: session.displayName,
            email: session.email,
            ...(embeddedWalletAddress ? { wallet_address: embeddedWalletAddress } : {}),
          },
          timeoutMs: ROLE_FETCH_TIMEOUT_MS,
        })
          .then((profileData) => {
            // Build from the CURRENT session, not the snapshot above: the
            // wallet re-sync below may have answered first, and writing the
            // snapshot back would undo it.
            const current = currentSessionFor(user.id);
            const next = current && sessionAfterProfileSync(current, profileData, { firstSync: true });
            if (next) setSession(next);
            if (profileData && typeof profileData.is_onboarded === "boolean") {
              setOnboarded(profileData.is_onboarded);
            }
            setProfileSynced(true);
            if (embeddedWalletAddress) {
              lastSyncedAddress.current = embeddedWalletAddress;
            }
          })
          .catch((err) => {
            console.error("[usePrivySync] Profile sync failed:", err);
            // Unknown renders as the short page; a failed sync falls back to
            // today's page instead of leaving it there all session.
            // Only when nothing has answered yet: a tier the wallet re-sync
            // already delivered is real and must not be replaced.
            const current = currentSessionFor(user.id);
            if (current && current.tier === undefined) setSession({ ...current, tier: syncedTier(null) });
            setProfileSynced(true);
          });
      }

      // Re-sync the embedded EOA address once it becomes available.
      // This updates wallet_address in the DB to match the on-chain
      // msg.sender. Only fires if the initial sync didn't already
      // include the EOA.
      if (embeddedWalletAddress && lastSyncedAddress.current !== embeddedWalletAddress) {
        lastSyncedAddress.current = embeddedWalletAddress;
        callEdgeFunction<{ role?: string; tier?: string; is_onboarded?: boolean }>("profiles", {
          body: {
            wallet_address: embeddedWalletAddress,
          },
        })
          .then((profileData) => {
            // Same as the initial sync: the two run concurrently on a load
            // where the wallet is already known, so read the current session.
            const current = currentSessionFor(user.id);
            const next = current && sessionAfterProfileSync(current, profileData, { firstSync: false });
            if (next) setSession(next);
          })
          .catch((err) => {
            console.error("[usePrivySync] Wallet address re-sync failed:", err);
            // Reset so next render retries the sync
            lastSyncedAddress.current = null;
          });
      }

      // Re-sync email if it arrived late (linked accounts loaded after
      // the initial profile sync already fired with an empty email).
      // Route through the edge function (same as initial sync) to bypass
      // RLS — the client-side Supabase JWT subject may not match user.id.
      if (lastSyncedAddress.current && !emailSynced.current && email) {
        emailSynced.current = true;
        callEdgeFunction("profiles", {
          body: { email },
        }).catch((err) => {
          console.error("[usePrivySync] Email re-sync failed:", err);
          // Reset so next render retries
          emailSynced.current = false;
        });
      }

      // Register user on-chain once per embedded EOA address. Gates on
      // both polygonClient (whose signer is the embedded EOA) and
      // embeddedWalletAddress directly so registration only fires once
      // the on-chain identity has resolved.
      if (embeddedWalletAddress && polygonClient && chainRegistered.current !== embeddedWalletAddress) {
        chainRegistered.current = embeddedWalletAddress;
        registerOnChain(polygonClient, session, embeddedWalletAddress).catch((err) => {
          console.error("[usePrivySync] On-chain registration failed:", err);
          // Reset so next render retries — without this the failure latches
          // for the whole session and every assignee-gated chain action
          // reverts NotRegistered.
          chainRegistered.current = null;
        });
      }
    } else {
      setSession(null);
      setProfileSynced(false);
      initialSyncDone.current = false;
      lastSyncedAddress.current = null;
      emailSynced.current = false;
      chainRegistered.current = null;
    }
  }, [ready, authenticated, user, setSession, setLoading, setProfileSynced, setOnboarded, polygonClient, embeddedWalletAddress]);
}

/**
 * The session in the store right now, if it still belongs to `userId`.
 * Profile-sync callbacks resolve after other writers (each other, a logout,
 * an account switch) may have run, so they build from this rather than the
 * session captured when the request was sent.
 */
function currentSessionFor(userId: string): AuthSession | null {
  const current = useAuthStore.getState().session;
  return current?.userId === userId ? current : null;
}

/**
 * Pick a stable embedded-wallet address for this user.
 *
 * Privy can attach more than one embedded wallet to a single account
 * (legacy + recreated, multi-chain, etc.). `wallets.find(...)` returns
 * whichever entry happens to be first in the array, which is not
 * guaranteed stable across renders or sessions — that non-determinism
 * caused wallet_address to flip in the DB and broke on-chain assignee
 * checks (DEV-242 / DEV-281).
 *
 * Selection rule:
 *   1. Prefer `user.wallet.address` (Privy's designated primary), if
 *      it appears in the embedded wallet list for this session.
 *   2. Otherwise, take the lexicographically smallest embedded address
 *      so the choice is deterministic across renders.
 */
function pickEmbeddedWalletAddress(
  user: ReturnType<typeof usePrivy>["user"],
  wallets: ReturnType<typeof useWallets>["wallets"],
): string | undefined {
  const embedded = wallets.filter((w) => w.walletClientType === "privy");
  if (embedded.length === 0) return undefined;

  const primary = user?.wallet?.address?.toLowerCase();
  if (primary) {
    const match = embedded.find((w) => w.address.toLowerCase() === primary);
    if (match) return match.address;
  }

  return embedded.reduce((min, w) =>
    w.address.toLowerCase() < min.address.toLowerCase() ? w : min,
  ).address;
}

async function registerOnChain(
  polygonClient: import("@/lib/polygon").PolygonClient,
  session: AuthSession,
  walletAddress: string,
): Promise<void> {
  // Skip if already registered
  const registered = await polygonClient.isRegistered(walletAddress);
  if (registered) return;

  const handle = session.displayName || session.email.split("@")[0] || "user";
  const profileJson = {
    displayName: session.displayName,
    email: session.email,
  };

  const receipt = await polygonClient.withContext(
    { flow: "userRegistration" },
    () => polygonClient.registerUser(handle, profileJson, [], "both"),
  );

  // Update Supabase to reflect on-chain registration. Match by `id`
  // (the Privy DID, stable from session) instead of `wallet_address`,
  // which may still be NULL in profiles during the brief window
  // between the initial profile insert and the wallet-address re-sync
  // — that race silently dropped this update before DEV-281.
  const { error, count } = await supabase
    .from("profiles")
    .update(
      { chain_registered: true, chain_tx_hash: receipt.hash },
      { count: "exact" },
    )
    .eq("id", session.userId);

  if (error) {
    console.error("[usePrivySync] chain_registered update failed:", error);
  } else if (count === 0) {
    console.warn(
      "[usePrivySync] chain_registered update matched zero rows for user",
      session.userId,
    );
  }

  console.log("[usePrivySync] User registered on-chain:", receipt.hash);
}
